import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search, LogOut, Plus, Trash2, UserCheck, MapPin, Filter,
  LayoutDashboard, Users, ChevronRight, Building2, Home as HomeIcon, Briefcase,
  Calendar, Phone, Mail, Eye, Wrench, ClipboardList, CheckCircle2, XCircle,
  Clock, Send, Award, Shield, FileText, type LucideIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Lead, Instalador, EstadoLead, TipoImovel,
  InstaladorComelit, PedidoOrcamento, PedidoExtensaoGarantia,
  Proposta,
  getLeads, getInstaladores, deleteLead, addInstalador, deleteInstalador, deleteInstaladorComelit,
  getInstalaldoresComelit, updateInstaladorComelit,
  getPedidosOrcamento, createPedidoOrcamento, updatePedidoOrcamento,
  getPedidosExtensao, updateExtensao,
  getPropostas,
  ESTADO_LABELS, ESTADO_COLORS, TIPO_IMOVEL_LABELS, DASHBOARD_PASSWORD,
  formatDate, formatDateShort, generateLeadCode, calcGarantia
} from '@/lib/index';

const TIPO_ICON: Record<TipoImovel, LucideIcon> = {
  predio: Building2,
  moradia: HomeIcon,
  escritorio: Briefcase,
};

type Tab = 'leads' | 'instaladores' | 'orcamentos' | 'garantias';

export default function Dashboard() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [tab, setTab] = useState<Tab>('leads');

  const [leads, setLeads] = useState<Lead[]>([]);
  const [instaladores, setInstaladores] = useState<Instalador[]>([]);
  const [instaladoresComelit, setInstalaldoresComelit] = useState<InstaladorComelit[]>([]);
  const [pedidosOrcamento, setPedidosOrcamento] = useState<PedidoOrcamento[]>([]);
  const [pedidosExtensao, setPedidosExtensao] = useState<PedidoExtensaoGarantia[]>([]);
  const [propostas, setPropostas] = useState<Proposta[]>([]);

  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState<EstadoLead | 'todos'>('todos');
  const [filterZona, setFilterZona] = useState('todos');

  // Dialogs
  const [showAddIns, setShowAddIns] = useState(false);
  const [newIns, setNewIns] = useState({ nome: '', zona: '', telefone: '', email: '' });
  const [showNewOrcamento, setShowNewOrcamento] = useState(false);
  const [newOrc, setNewOrc] = useState({ leadId: '', zona: '', descricao: '' });

  const loadData = useCallback(async () => {
    const [l, i, ic, po, pe, pr] = await Promise.all([
      getLeads(), getInstaladores(), getInstalaldoresComelit(),
      getPedidosOrcamento(), getPedidosExtensao(), getPropostas(),
    ]);
    setLeads(l); setInstaladores(i); setInstalaldoresComelit(ic);
    setPedidosOrcamento(po); setPedidosExtensao(pe); setPropostas(pr);
  }, []);

  useEffect(() => { if (authed) loadData(); }, [authed, loadData]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === DASHBOARD_PASSWORD) { setAuthed(true); setLoginError(''); }
    else setLoginError('Palavra-passe incorreta.');
  };

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleDeleteLead = async (id: string) => {
    await deleteLead(id);
    setConfirmDeleteId(null);
    loadData();
  };

  const handleAddInstalador = async () => {
    if (!newIns.nome || !newIns.zona) return;
    await addInstalador(newIns);
    setNewIns({ nome: '', zona: '', telefone: '', email: '' });
    setShowAddIns(false); loadData();
  };

  const handleDeleteInstalador = async (id: string) => {
    if (!confirm('Eliminar?')) return;
    await deleteInstalador(id); loadData();
  };

  const handleAprovarComelit = async (id: string, status: 'aprovado' | 'rejeitado') => {
    await updateInstaladorComelit(id, { status });
    const comelit = instaladoresComelit.find((i) => i.id === id);
    if (status === 'aprovado' && comelit) {
      const jaExiste = instaladores.find((i) => i.email === comelit.email);
      if (!jaExiste) {
        await addInstalador({ nome: comelit.nome, zona: comelit.zona, telefone: comelit.telefone, email: comelit.email });
      }
      // Email de aprovação ao instalador
      await fetch('https://api.web3forms.com/submit', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: import.meta.env.VITE_WEB3FORMS_KEY,
          to_email: comelit.email,
          subject: 'AT CONTERA — A sua conta foi aprovada! ✅',
          from_name: 'AT CONTERA',
          message: `Olá ${comelit.nome},\n\nA sua conta de Instalador Certificado Comelit AT CONTERA foi APROVADA!\n\nJá pode aceder ao portal e começar a receber pedidos de orçamento na zona ${comelit.zona}.\n\n🔗 Aceder ao portal: ${window.location.origin}/#/instalador\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━\nO que fazer agora:\n1. Faça login com o email e palavra-passe que registou\n2. Veja os pedidos de orçamento disponíveis na sua zona\n3. Manifeste interesse e aguarde contacto da AT CONTERA\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n\nBem-vindo à rede AT CONTERA!\n\nAT CONTERA | comercial@contera.pt`,
        }),
      });
    }
    if (status === 'rejeitado' && comelit) {
      await fetch('https://api.web3forms.com/submit', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: import.meta.env.VITE_WEB3FORMS_KEY,
          to_email: comelit.email,
          subject: 'AT CONTERA — Atualização do seu registo',
          from_name: 'AT CONTERA',
          message: `Olá ${comelit.nome},\n\nApós análise do seu pedido de registo, não foi possível aprovar a sua conta neste momento.\n\nPara mais informações ou para submeter nova candidatura, entre em contacto connosco.\n\nAT CONTERA | comercial@contera.pt`,
        }),
      });
    }
    loadData();
  };

  const handleCreateOrcamento = async () => {
    if (!newOrc.leadId || !newOrc.zona || !newOrc.descricao) return;
    const pedido = await createPedidoOrcamento(newOrc);
    // Notificar instaladores da zona via email
    const instaladoresZona = instaladoresComelit.filter(
      (i) => i.status === 'aprovado' && i.zona === newOrc.zona
    );
    const lead = leads.find((l) => l.id.startsWith(newOrc.leadId.replace('CTR-', '').toLowerCase().slice(0,8)));
    try {
      for (const ins of instaladoresZona) {
        await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            access_key: import.meta.env.VITE_WEB3FORMS_KEY,
            subject: `[AT CONTERA] Novo pedido de orçamento na sua zona — ${newOrc.zona}`,
            from_name: 'AT CONTERA',
            replyto: 'comercial@contera.pt',
            message: [
              `Olá ${ins.nome},`,
              ``,
              `Existe um novo pedido de orçamento na sua zona (${newOrc.zona}).`,
              ``,
              `📋 REFERÊNCIA: ${pedido.id.slice(0,8).toUpperCase()}`,
              `📍 ZONA: ${newOrc.zona}`,
              `📝 DESCRIÇÃO: ${newOrc.descricao}`,
              lead ? `🏠 IMÓVEL: ${lead.localidade} · ${TIPO_IMOVEL_LABELS[lead.tipoImovel]}` : '',
              ``,
              `─────────────────────────────────────────────`,
              `COMO FUNCIONA:`,
              `1️⃣  Cliente fez o pedido de instalação`,
              `2️⃣  AT CONTERA contacta os Instaladores Certificados Comelit da zona`,
              `3️⃣  AT CONTERA envia as propostas ao cliente para escolha`,
              `4️⃣  AT CONTERA garante todos os trabalhos com garantia de 3 anos`,
              `─────────────────────────────────────────────`,
              `💳 PAGAMENTOS — TUDO PELA AT CONTERA, SEM EXCEPÇÕES`,
              ``,
              `⚠️  NÃO ACEITE NEM SOLICITE QUALQUER PAGAMENTO AO CLIENTE.`,
              ``,
              `   → O cliente paga 30% de sinal à AT CONTERA na adjudicação`,
              `   → A AT CONTERA fornece o material ao instalador`,
              `   → O cliente paga os 70% restantes à AT CONTERA na conclusão`,
              `   → A AT CONTERA paga-lhe no prazo de 48 horas após conclusão`,
              ``,
              `   Não existe qualquer dinheiro a circular fora da AT CONTERA.`,
              `   Este serviço é 100% gratuito e transparente para ambas as partes.`,
              ``,
              `─────────────────────────────────────────────`,
              `• Todos os trabalhos são supervisionados pela AT CONTERA`,
              `• Garantia oficial de 3 anos em todos os trabalhos`,
              `• 66 anos de experiência no setor`,
              ``,
              `Para responder a este pedido, aceda à sua área de instalador em /instalador`,
            ].filter(Boolean).join('\n'),
          }),
        });
      }
    } catch { /* silencioso */ }
    setNewOrc({ leadId: '', zona: '', descricao: '' });
    setShowNewOrcamento(false);
    loadData();
  };

  const zonas = [...new Set(instaladores.map((i) => i.zona))].sort();

  const filteredLeads = leads.filter((l) => {
    const matchSearch =
      !search ||
      l.nome.toLowerCase().includes(search.toLowerCase()) ||
      l.email.toLowerCase().includes(search.toLowerCase()) ||
      l.localidade.toLowerCase().includes(search.toLowerCase()) ||
      generateLeadCode(l.id).toLowerCase().includes(search.toLowerCase());
    const matchEstado = filterEstado === 'todos' || l.estado === filterEstado;
    const matchZona = filterZona === 'todos' || instaladores.find((i) => i.id === l.instaladorId)?.zona === filterZona;
    return matchSearch && matchEstado && matchZona;
  });

  const statCounts = {
    total: leads.length,
    novos: leads.filter((l) => l.estado === 'novo').length,
    emCurso: leads.filter((l) => l.estado === 'obra_em_curso').length,
    concluidos: leads.filter((l) => l.estado === 'concluido').length,
  };

  const pendentesCount = instaladoresComelit.filter((i) => i.status === 'pendente').length;
  const extensoesPendentes = pedidosExtensao.filter((e) => e.estado === 'pendente').length;

  // ── Nav items ─────────────────────────────────────────────────────────────────
  const navItems: { id: Tab; icon: LucideIcon; label: string; count?: number; alert?: boolean }[] = [
    { id: 'leads', icon: LayoutDashboard, label: 'Pedidos', count: leads.length },
    { id: 'instaladores', icon: Users, label: 'Instaladores', count: instaladoresComelit.length, alert: pendentesCount > 0 },
    { id: 'orcamentos', icon: ClipboardList, label: 'Orçamentos', count: pedidosOrcamento.length },
    { id: 'garantias', icon: CheckCircle2, label: 'Garantias', count: pedidosExtensao.length, alert: extensoesPendentes > 0 },
  ];

  // ── Login ─────────────────────────────────────────────────────────────────────
  if (!authed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ type: 'spring', stiffness: 300, damping: 35 }} className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center mx-auto mb-4">
              <span className="text-primary-foreground font-bold text-2xl">C</span>
            </div>
            <h1 className="text-2xl font-bold text-foreground">AT CONTERA</h1>
            <p className="text-muted-foreground text-sm mt-1">Dashboard Interno</p>
          </div>
          <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="pwd">Palavra-passe</Label>
                <Input id="pwd" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
                {loginError && <p className="text-destructive text-xs">{loginError}</p>}
              </div>
              <Button type="submit" className="w-full">Entrar</Button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  // ── Dashboard ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="w-56 shrink-0 border-r border-border bg-card flex flex-col py-6 px-3 hidden lg:flex">
        <div className="flex items-center gap-2 px-3 mb-8">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-sm">C</span>
          </div>
          <div>
            <p className="font-bold text-sm text-foreground">AT CONTERA</p>
            <p className="text-xs text-muted-foreground">Dashboard</p>
          </div>
        </div>
        <nav className="space-y-1 flex-1">
          {navItems.map((item) => (
            <button key={item.id} onClick={() => setTab(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === item.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground'}`}>
              <item.icon size={16} />
              {item.label}
              <span className={`ml-auto text-xs px-1.5 py-0.5 rounded-full ${tab === item.id ? 'bg-primary-foreground/20 text-primary-foreground' : item.alert ? 'bg-red-100 text-red-600' : 'bg-muted text-muted-foreground'}`}>
                {item.alert ? (item.id === 'instaladores' ? pendentesCount : extensoesPendentes) : item.count}
              </span>
            </button>
          ))}
        </nav>
        <button onClick={() => setAuthed(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-destructive rounded-lg hover:bg-destructive/8 transition-colors mt-auto">
          <LogOut size={15} />Sair
        </button>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden flex items-center justify-between px-3 py-3 border-b border-border bg-card gap-2 overflow-x-auto">
          <div className="flex items-center gap-2 shrink-0">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-xs">C</span>
            </div>
          </div>
          <div className="flex gap-1 overflow-x-auto">
            {navItems.map((item) => (
              <Button key={item.id} variant={tab === item.id ? 'default' : 'ghost'} size="sm" className="text-xs shrink-0" onClick={() => setTab(item.id)}>
                {item.label}
                {item.alert && <span className="ml-1 w-2 h-2 rounded-full bg-red-500 inline-block" />}
              </Button>
            ))}
            <Button variant="ghost" size="sm" onClick={() => setAuthed(false)}><LogOut size={14} /></Button>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 overflow-auto">

          {/* ── RESUMO INICIAL (sempre visível no topo) ── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Pedidos novos', value: statCounts.novos, color: 'text-blue-600', bg: 'bg-blue-50', onClick: () => setTab('leads') },
              { label: 'Propostas aprovadas', value: propostas.filter(p=>p.estadoCliente==='aprovado').length, color: 'text-green-600', bg: 'bg-green-50', onClick: () => setTab('orcamentos') },
              { label: 'Instaladores pendentes', value: instaladoresComelit.filter(i=>i.status==='pendente').length, color: 'text-yellow-600', bg: 'bg-yellow-50', onClick: () => setTab('instaladores') },
              { label: 'Garantias ativas', value: leads.filter(l=>l.estado==='concluido'&&l.dataConclusao).length, color: 'text-purple-600', bg: 'bg-purple-50', onClick: () => setTab('garantias') },
            ].map((s) => (
              <button key={s.label} onClick={s.onClick} className={`${s.bg} border border-border rounded-xl p-4 text-left hover:opacity-80 transition-opacity cursor-pointer`}>
                <p className={`text-2xl font-extrabold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">{s.label}</p>
              </button>
            ))}
          </div>

          {/* ── LEADS ── */}
          {tab === 'leads' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                {[
                  { label: 'Total', value: statCounts.total, color: 'text-foreground' },
                  { label: 'Novos', value: statCounts.novos, color: 'text-blue-600' },
                  { label: 'Em Curso', value: statCounts.emCurso, color: 'text-orange-600' },
                  { label: 'Concluídos', value: statCounts.concluidos, color: 'text-green-600' },
                ].map((s) => (
                  <div key={s.label} className="bg-card border border-border rounded-xl p-4">
                    <p className="text-xs text-muted-foreground">{s.label}</p>
                    <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3 mb-5">
                <div className="relative flex-1 min-w-48">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input className="pl-9" placeholder="Nome, email, localidade, referência..." value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <Select value={filterEstado} onValueChange={(v) => setFilterEstado(v as EstadoLead | 'todos')}>
                  <SelectTrigger className="w-44"><Filter size={13} className="mr-1.5 text-muted-foreground" /><SelectValue placeholder="Estado" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos os estados</SelectItem>
                    {(Object.entries(ESTADO_LABELS) as [EstadoLead, string][]).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filterZona} onValueChange={setFilterZona}>
                  <SelectTrigger className="w-40"><MapPin size={13} className="mr-1.5 text-muted-foreground" /><SelectValue placeholder="Zona" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todas as zonas</SelectItem>
                    {zonas.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {filteredLeads.length === 0 ? (
                <div className="text-center py-20 text-muted-foreground">
                  <LayoutDashboard size={40} className="mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Sem pedidos encontrados</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredLeads.map((lead) => {
                    const TipoIcon = TIPO_ICON[lead.tipoImovel];
                    const insAtribuidos = instaladores.filter((i) =>
                      lead.instaladorIds?.includes(i.id) || lead.instaladorId === i.id
                    );
                    return (
                      <motion.div key={lead.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                        className="bg-card border border-border rounded-xl p-4 flex items-center gap-4 hover:border-primary/30 transition-colors group">
                        <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                          <TipoIcon size={18} className="text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm text-foreground truncate">{lead.nome}</span>
                            <span className="font-mono text-xs text-muted-foreground">{generateLeadCode(lead.id)}</span>
                            <Badge className={`text-xs border ${ESTADO_COLORS[lead.estado]}`} variant="outline">{ESTADO_LABELS[lead.estado]}</Badge>
                            {lead.numeroFatura && <Badge variant="outline" className="text-xs border-green-200 bg-green-50 text-green-700">Fatura: {lead.numeroFatura}</Badge>}
                            {/* badges financeiros */}
                            {lead.sinalPago && !lead.pagamentoFinalPago && (
                              <Badge className="text-xs border border-green-200 bg-green-50 text-green-700">💰 Sinal 30%</Badge>
                            )}
                            {lead.pagamentoFinalPago && !lead.instaladorPago && (
                              <Badge className="text-xs border border-orange-200 bg-orange-50 text-orange-700 animate-pulse">⚠ Pagar instalador</Badge>
                            )}
                            {lead.instaladorPago && (
                              <Badge className="text-xs border border-blue-200 bg-blue-50 text-blue-700">✓ Instalador pago</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 flex-wrap">
                            <span className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={11} />{lead.localidade} · {TIPO_IMOVEL_LABELS[lead.tipoImovel]}</span>
                            <span className="text-xs text-muted-foreground flex items-center gap-1"><Calendar size={11} />Visita: {formatDateShort(lead.dataVisita)}</span>
                            {insAtribuidos.length > 0 && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <UserCheck size={11} />
                                {insAtribuidos.map((i) => i.nome).join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => navigate(`/dashboard/lead/${lead.id}`)}>
                            <Eye size={15} />
                          </Button>
                          {confirmDeleteId === lead.id ? (
                            <div className="flex items-center gap-1.5 bg-red-50 border border-red-200 rounded-lg px-2 py-1">
                              <span className="text-xs text-red-700 font-medium">Eliminar?</span>
                              <Button size="sm" className="h-6 px-2 text-xs bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDeleteLead(lead.id)}>Sim</Button>
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setConfirmDeleteId(null)}>Não</Button>
                            </div>
                          ) : (
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:bg-destructive/10" onClick={() => setConfirmDeleteId(lead.id)}>
                              <Trash2 size={14} />
                            </Button>
                          )}
                          <ChevronRight size={16} className="text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => navigate(`/dashboard/lead/${lead.id}`)} />
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}

          {/* ── INSTALADORES INTERNOS ── */}
          {tab === 'instaladores' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-foreground">Instaladores</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">Instaladores registados pelo portal e adicionados manualmente.</p>
                </div>
                <Button onClick={() => setShowAddIns(true)} size="sm"><Plus size={15} className="mr-1.5" />Adicionar manualmente</Button>
              </div>
              {pendentesCount > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 mb-4 flex items-center gap-2">
                  <Clock size={16} className="text-yellow-600 shrink-0" />
                  <p className="text-sm text-yellow-700 font-medium">{pendentesCount} pedido{pendentesCount > 1 ? 's' : ''} de registo em análise — aprovação pendente</p>
                </div>
              )}
              {instaladoresComelit.length === 0 ? (
                <div className="text-center py-20 text-muted-foreground"><Users size={40} className="mx-auto mb-3 opacity-30" /><p>Nenhum instalador ainda.</p><p className="text-sm mt-1">Adicione manualmente ou aguarde registos pelo portal.</p></div>
              ) : (
                <div className="space-y-3">
                  {instaladoresComelit.map((ins) => (
                    <div key={ins.id} className="bg-card border border-border rounded-xl p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <p className="font-semibold text-sm text-foreground">{ins.nome}</p>
                            <Badge className={`text-xs border ${ins.status === 'aprovado' ? 'bg-green-100 text-green-700 border-green-200' : ins.status === 'pendente' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' : 'bg-red-100 text-red-700 border-red-200'}`}>
                              {ins.status === 'aprovado' ? '✓ Aprovado' : ins.status === 'pendente' ? '⏳ Pendente' : '✗ Rejeitado'}
                            </Badge>
                            {ins.temSeguro && <Badge className="text-xs bg-blue-50 text-blue-700 border-blue-200 border">Seguro ✓</Badge>}
                            {ins.proximaCertificacao && (() => {
                              const dias = Math.round((new Date(ins.proximaCertificacao).getTime() - Date.now()) / 86400000);
                              if (dias < 0) return <Badge className="text-xs bg-red-100 text-red-700 border-red-200 border">Cert. expirada</Badge>;
                              if (dias <= 30) return <Badge className="text-xs bg-orange-100 text-orange-700 border-orange-200 border">Cert. expira em {dias}d</Badge>;
                              return <Badge className="text-xs bg-green-50 text-green-700 border-green-200 border">Cert. válida</Badge>;
                            })()}
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><MapPin size={11} />{ins.zona}</span>
                            <span className="flex items-center gap-1"><Phone size={11} />{ins.telefone}</span>
                            <span className="flex items-center gap-1"><Mail size={11} />{ins.email}</span>
                            <span>NIF: {ins.nif}</span>
                            {ins.numeroClienteContera && <span>Nº AT CONTERA: {ins.numeroClienteContera}</span>}
                            <span>Registado: {formatDate(ins.criadoEm)}</span>
                          </div>
                          {/* Próxima Certificação */}
                          <div className="mt-3 pt-3 border-t border-border flex items-center gap-3">
                            <Award size={13} className="text-muted-foreground shrink-0" />
                            <span className="text-xs text-muted-foreground shrink-0">Próxima certificação Comelit:</span>
                            <input
                              type="date"
                              className="text-xs border border-border rounded-md px-2 py-1 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                              value={ins.proximaCertificacao ?? ''}
                              onChange={async (e) => {
                                await updateInstaladorComelit(ins.id, { proximaCertificacao: e.target.value || undefined });
                                loadData();
                              }}
                            />
                            {ins.proximaCertificacao && (
                              <span className="text-xs text-muted-foreground">{formatDate(ins.proximaCertificacao)}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3 pt-3 border-t border-border flex-wrap">
                          {ins.status === 'pendente' && (
                            <button
                              style={{background:'#16a34a',color:'#fff',border:'none',borderRadius:'6px',padding:'6px 14px',fontSize:'12px',fontWeight:600,cursor:'pointer'}}
                              onClick={() => handleAprovarComelit(ins.id, 'aprovado')}
                            >✓ Aprovar</button>
                          )}
                          {ins.status === 'pendente' && (
                            <button
                              style={{background:'#fff',color:'#dc2626',border:'1px solid #dc2626',borderRadius:'6px',padding:'6px 14px',fontSize:'12px',fontWeight:600,cursor:'pointer'}}
                              onClick={() => handleAprovarComelit(ins.id, 'rejeitado')}
                            >✗ Rejeitar</button>
                          )}
                          {ins.status === 'aprovado' && (
                            <button
                              style={{background:'#f3f4f6',color:'#374151',border:'1px solid #d1d5db',borderRadius:'6px',padding:'6px 14px',fontSize:'12px',fontWeight:600,cursor:'pointer'}}
                              onClick={() => handleAprovarComelit(ins.id, 'rejeitado')}
                            >Suspender</button>
                          )}
                          {ins.status === 'rejeitado' && (
                            <button
                              style={{background:'#f3f4f6',color:'#374151',border:'1px solid #d1d5db',borderRadius:'6px',padding:'6px 14px',fontSize:'12px',fontWeight:600,cursor:'pointer'}}
                              onClick={() => handleAprovarComelit(ins.id, 'aprovado')}
                            >Reativar</button>
                          )}
                          <button
                            style={{background:'#fee2e2',color:'#dc2626',border:'1px solid #fca5a5',borderRadius:'6px',padding:'6px 14px',fontSize:'12px',fontWeight:600,cursor:'pointer'}}
                            onClick={async () => {
                              if (!window.confirm('Eliminar ' + ins.nome + '? Esta ação é irreversível.')) return;
                              await deleteInstaladorComelit(ins.id);
                              loadData();
                            }}
                          >🗑 Eliminar</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* ── ORÇAMENTOS ── */}
          {tab === 'orcamentos' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">

              {/* Estatísticas rápidas */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Propostas totais', val: propostas.length, color: 'bg-primary/10 text-primary' },
                  { label: 'Aprovadas pelo cliente', val: propostas.filter(p => p.estadoCliente === 'aprovado').length, color: 'bg-green-100 text-green-700' },
                  { label: 'Pendentes aprovação', val: propostas.filter(p => p.estadoCliente !== 'aprovado').length, color: 'bg-yellow-100 text-yellow-700' },
                  { label: 'Pedidos em aberto', val: pedidosOrcamento.filter(p => p.estado === 'aberto').length, color: 'bg-blue-100 text-blue-700' },
                ].map(s => (
                  <div key={s.label} className="bg-card border border-border rounded-xl p-4 text-center">
                    <p className={`text-2xl font-extrabold mb-1 ${s.color.split(' ')[1]}`}>{s.val}</p>
                    <p className="text-xs text-muted-foreground leading-tight">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Propostas dos instaladores */}
              <div className="bg-card border border-border rounded-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                  <div>
                    <h2 className="font-bold text-foreground">Propostas dos Instaladores</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">{propostas.length} proposta{propostas.length !== 1 ? 's' : ''} submetida{propostas.length !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="flex gap-2">
                    <Badge className="text-xs bg-green-100 text-green-700 border-green-200 border">{propostas.filter(p=>p.estadoCliente==='aprovado').length} aprovada{propostas.filter(p=>p.estadoCliente==='aprovado').length!==1?'s':''}</Badge>
                    <Badge className="text-xs bg-yellow-100 text-yellow-700 border-yellow-200 border">{propostas.filter(p=>p.estadoCliente!=='aprovado').length} pendente{propostas.filter(p=>p.estadoCliente!=='aprovado').length!==1?'s':''}</Badge>
                  </div>
                </div>
                {propostas.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground"><FileText size={32} className="mx-auto mb-2 opacity-30" /><p className="text-sm">Nenhuma proposta ainda.</p></div>
                ) : (
                  <div className="divide-y divide-border">
                    {[...propostas].sort((a,b) => b.criadoEm.localeCompare(a.criadoEm)).map(p => (
                      <div key={p.id} className={`px-5 py-3 flex items-center gap-4 flex-wrap hover:bg-muted/30 transition-colors ${p.estadoCliente === 'aprovado' ? 'bg-green-50/50' : ''}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <span className="font-mono text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">{p.refCTR}</span>
                            <span className="text-sm font-semibold text-foreground">{p.instaladorNome}</span>
                            {p.clienteNome && <span className="text-xs text-muted-foreground">· 👤 {p.clienteNome}</span>}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                            <span>{p.tipoSistema === 'video' ? '📹 Vídeo' : '🔊 Áudio'}{p.numInquilinos ? ` · ${p.numInquilinos} inq.` : ''}</span>
                            {p.obraMorada && <span>📍 {p.obraMorada}</span>}
                            <span>📅 {new Date(p.criadoEm).toLocaleDateString('pt-PT')}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="font-bold text-primary">{p.totalEuros.toLocaleString('pt-PT',{minimumFractionDigits:2})} €</span>
                          {p.estadoCliente === 'aprovado'
                            ? <span className="text-xs bg-green-100 text-green-700 border border-green-200 px-2 py-1 rounded-full font-medium">✓ Aprovado</span>
                            : <span className="text-xs bg-yellow-100 text-yellow-700 border border-yellow-200 px-2 py-1 rounded-full font-medium">⏳ Pendente</span>
                          }
                          {p.htmlContent && (
                            <button onClick={() => { const w = window.open('','_blank'); if(w){w.document.write(p.htmlContent);w.document.close();} }}
                              className="text-xs bg-primary text-primary-foreground px-3 py-1.5 rounded-lg hover:bg-primary/90 transition-colors">
                              👁 Ver
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Pedidos de Orçamento */}
              <div className="bg-card border border-border rounded-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                  <div>
                    <h2 className="font-bold text-foreground">Pedidos de Orçamento</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">Notificações enviadas aos instaladores da zona</p>
                  </div>
                  <Button onClick={() => setShowNewOrcamento(true)} size="sm"><Plus size={15} className="mr-1.5" />Novo Pedido</Button>
                </div>
                {pedidosOrcamento.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground"><ClipboardList size={32} className="mx-auto mb-2 opacity-30" /><p className="text-sm">Nenhum pedido criado.</p></div>
                ) : (
                  <div className="divide-y divide-border">
                    {pedidosOrcamento.map((p) => {
                      const lead = leads.find((l) => l.id.startsWith(p.leadId));
                      return (
                        <div key={p.id} className="px-5 py-3">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className={`text-xs border ${p.estado === 'aberto' ? 'bg-green-100 text-green-700 border-green-200' : p.estado === 'em_selecao' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' : 'bg-muted text-muted-foreground border-border'}`}>
                                {p.estado === 'aberto' ? '● Aberto' : p.estado === 'em_selecao' ? '● Em Seleção' : '● Fechado'}
                              </Badge>
                              <span className="font-semibold text-sm text-foreground">{p.descricao}</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-xs text-muted-foreground">{p.respostas.length} resp.</span>
                              {p.estado !== 'fechado' && (
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={async () => { await updatePedidoOrcamento(p.id, { estado: 'fechado' }); loadData(); }}>Fechar</Button>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-4 text-xs text-muted-foreground mb-2">
                            <span className="flex items-center gap-1"><MapPin size={10} />{p.zona}</span>
                            {lead && <span>{lead.localidade}</span>}
                            <span>{formatDate(p.criadoEm)}</span>
                          </div>
                          {p.respostas.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {p.respostas.map((r, idx) => {
                                const ins = instaladoresComelit.find((i) => i.id === r.instaladorId);
                                return (
                                  <span key={r.instaladorId} className={`text-xs px-2.5 py-1 rounded-full border font-medium ${r.selecionado ? 'bg-green-100 text-green-700 border-green-200' : 'bg-muted text-muted-foreground border-border'}`}>
                                    #{idx+1} {ins?.nome ?? '—'} {r.selecionado ? '✓' : ''}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* ── GARANTIAS ── */}
          {tab === 'garantias' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">

              {/* Garantias Ativas */}
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-bold text-foreground">Garantias Ativas</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">Controlo das garantias de 3 anos por código de pedido. Marque um pedido como "Concluído" para ativar a garantia.</p>
                </div>
                {leads.filter((l) => l.estado === 'concluido' && l.dataConclusao).length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground bg-muted/30 rounded-2xl border border-dashed border-border">
                    <Shield size={36} className="mx-auto mb-3 opacity-30" />
                    <p className="text-sm">Nenhuma garantia ativa ainda.</p>
                    <p className="text-xs mt-1">As garantias ativam quando um pedido é marcado como "Concluído".</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {leads.filter((l) => l.estado === 'concluido' && l.dataConclusao).map((l) => {
                      const g = calcGarantia(l.dataConclusao!);
                      return (
                        <div key={l.id} className="bg-card border border-border rounded-xl p-4">
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div>
                              <p className="font-semibold text-sm text-foreground">{l.nome}</p>
                              <p className="font-mono text-xs text-muted-foreground">{generateLeadCode(l.id)}</p>
                            </div>
                            <Badge className={`text-xs border ${g.daysLeft <= 0 ? 'bg-red-100 text-red-700 border-red-200' : g.daysLeft <= 90 ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-green-100 text-green-700 border-green-200'}`}>
                              {g.daysLeft <= 0 ? 'Expirada' : `${g.daysLeft} dias restantes`}
                            </Badge>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2 mb-2 overflow-hidden">
                            <div className={`h-2 rounded-full ${g.percent > 50 ? 'bg-green-500' : g.percent > 20 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${g.percent}%` }} />
                          </div>
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>Início: {formatDate(l.dataConclusao!)}</span>
                            <span>Expira: {formatDate(g.expiry.toISOString())}</span>
                          </div>
                          {l.numeroFatura && (
                            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                              <FileText size={11} />Fatura: <span className="font-mono font-medium">{l.numeroFatura}</span>
                            </p>
                          )}
                          <Button size="sm" variant="outline" className="mt-3 h-7 text-xs" onClick={() => navigate(`/dashboard/lead/${l.id}`)}>
                            Ver detalhes →
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Pedidos sem garantia ainda */}
              <div>
                <div className="mb-4">
                  <h3 className="text-base font-semibold text-foreground">Pedidos sem garantia ativa</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Pedidos em curso — clique para gerir e ativar a garantia quando a obra estiver concluída.</p>
                </div>
                {leads.filter((l) => l.estado !== 'concluido' && l.estado !== 'cancelado').length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">Sem pedidos em curso.</p>
                ) : (
                  <div className="space-y-2">
                    {leads.filter((l) => l.estado !== 'concluido' && l.estado !== 'cancelado').map((l) => (
                      <div key={l.id} className="flex items-center justify-between gap-3 bg-muted/40 border border-border rounded-xl px-4 py-3">
                        <div>
                          <p className="font-medium text-sm text-foreground">{l.nome}</p>
                          <p className="font-mono text-xs text-muted-foreground">{generateLeadCode(l.id)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`text-xs border ${ESTADO_COLORS[l.estado]}`}>{ESTADO_LABELS[l.estado]}</Badge>
                          <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" onClick={() => navigate(`/dashboard/lead/${l.id}`)}>Gerir →</Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive hover:bg-destructive/10 shrink-0" onClick={async () => { if (!confirm(`Eliminar pedido de ${l.nome}?`)) return; await deleteLead(l.id); loadData(); }}><Trash2 size={13} /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Pedidos de Extensão */}
              <div>
                <div className="mb-4">
                  <h3 className="text-base font-semibold text-foreground">Pedidos de Extensão</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Análise e resposta aos pedidos de extensão submetidos pelos clientes.</p>
                </div>
                {pedidosExtensao.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground bg-muted/30 rounded-2xl border border-dashed border-border">
                    <CheckCircle2 size={28} className="mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Sem pedidos de extensão.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pedidosExtensao.map((ext) => {
                      const lead = leads.find((l) => l.id === ext.leadId);
                      return (
                        <div key={ext.id} className="bg-card border border-border rounded-xl p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap mb-2">
                                {lead && <span className="font-semibold text-sm text-foreground">{lead.nome}</span>}
                                {lead && <span className="font-mono text-xs text-muted-foreground">{generateLeadCode(lead.id)}</span>}
                                <Badge className={`text-xs border ${ext.estado === 'pendente' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' : ext.estado === 'aprovado' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-red-100 text-red-700 border-red-200'}`}>
                                  {ext.estado === 'pendente' ? '⏳ Pendente' : ext.estado === 'aprovado' ? '✓ Aprovado' : '✗ Rejeitado'}
                                </Badge>
                              </div>
                              <p className="text-sm text-foreground mb-1"><span className="font-medium">Motivo:</span> {ext.motivo}</p>
                              <p className="text-xs text-muted-foreground">Submetido: {formatDate(ext.criadoEm)}</p>
                              {lead?.localidade && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin size={11} />{lead.localidade}</p>}
                            </div>
                            <div className="flex gap-2 shrink-0 flex-wrap justify-end">
                              {ext.estado === 'pendente' && (<>
                                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white h-8 px-3" onClick={async () => { await updateExtensao(ext.id, 'aprovado'); loadData(); }}><CheckCircle2 size={14} className="mr-1" />Aprovar</Button>
                                <Button size="sm" variant="outline" className="h-8 px-3 text-destructive border-destructive/30 hover:bg-destructive/10" onClick={async () => { await updateExtensao(ext.id, 'rejeitado'); loadData(); }}><XCircle size={14} className="mr-1" />Rejeitar</Button>
                              </>)}
                              <Button size="sm" variant="ghost" className="h-8 px-2 text-destructive hover:bg-destructive/10" onClick={async () => { if (!confirm('Eliminar este pedido de extensão?')) return; await updateExtensao(ext.id, 'rejeitado'); loadData(); }}><Trash2 size={13} /></Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </motion.div>
          )}

          {/* ── PROPOSTAS ── */}

        </main>
      </div>

      {/* Dialog Instalador */}
      <Dialog open={showAddIns} onOpenChange={setShowAddIns}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Novo Instalador</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nome *</Label><Input placeholder="João Silva" value={newIns.nome} onChange={(e) => setNewIns(p => ({ ...p, nome: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Zona *</Label><Input placeholder="Lisboa" value={newIns.zona} onChange={(e) => setNewIns(p => ({ ...p, zona: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Telefone</Label><Input placeholder="912 345 678" value={newIns.telefone} onChange={(e) => setNewIns(p => ({ ...p, telefone: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input placeholder="joao@instala.pt" value={newIns.email} onChange={(e) => setNewIns(p => ({ ...p, email: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddIns(false)}>Cancelar</Button>
            <Button onClick={handleAddInstalador} disabled={!newIns.nome || !newIns.zona}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Orçamento */}
      <Dialog open={showNewOrcamento} onOpenChange={setShowNewOrcamento}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Novo Pedido de Orçamento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Lead (referência) *</Label>
              <Select value={newOrc.leadId} onValueChange={(v) => setNewOrc(p => ({ ...p, leadId: v }))}>
                <SelectTrigger><SelectValue placeholder="Selecionar pedido..." /></SelectTrigger>
                <SelectContent>
                  {leads.filter(l => !['concluido','cancelado'].includes(l.estado)).map((l) => (
                    <SelectItem key={l.id} value={l.id}>{generateLeadCode(l.id)} — {l.nome} ({l.localidade})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Zona a notificar *</Label>
              <Select value={newOrc.zona} onValueChange={(v) => setNewOrc(p => ({ ...p, zona: v }))}>
                <SelectTrigger><SelectValue placeholder="Zona..." /></SelectTrigger>
                <SelectContent>
                  {[...new Set(instaladoresComelit.filter(i => i.status === 'aprovado').map(i => i.zona))].sort().map((z) => (
                    <SelectItem key={z} value={z}>{z} ({instaladoresComelit.filter(i => i.zona === z && i.status === 'aprovado').length} instaladores)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Descrição *</Label>
              <Textarea placeholder="Ex: videoporteiro morada, substituição completa, andares..." className="resize-none h-20" value={newOrc.descricao} onChange={(e) => setNewOrc(p => ({ ...p, descricao: e.target.value }))} />
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Send size={11} />
              {newOrc.zona ? `${instaladoresComelit.filter(i => i.zona === newOrc.zona && i.status === 'aprovado').length} instalador(es) da zona serão notificados por email.` : 'Selecione uma zona para ver o número de instaladores.'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewOrcamento(false)}>Cancelar</Button>
            <Button onClick={handleCreateOrcamento} disabled={!newOrc.leadId || !newOrc.zona || !newOrc.descricao}>
              <Send size={14} className="mr-1.5" />Criar e Notificar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
